import type { HTMLAttributes, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { brandColour } from "../lib/branding";
import type { Status } from "../lib/format";
import { fmtRelative, parseFrappeDatetime } from "../lib/format";

export const STATUS_BG: Record<Status, string> = {
  Green: "bg-status-green",
  Amber: "bg-status-amber",
  Red: "bg-status-red",
  Grey: "bg-brand-grey",
};
export const STATUS_TEXT: Record<Status, string> = {
  Green: "text-status-green",
  Amber: "text-status-amber",
  Red: "text-status-red",
  Grey: "text-brand-grey",
};

export function StatusDot({ status, className = "" }: { status: Status; className?: string }) {
  const { t } = useTranslation();
  return <span aria-label={t(`status.${status}`)} title={t(`status.${status}`)} className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_BG[status]} ${className}`} />;
}

export function SeverityBadge({ severity }: { severity: "Amber" | "Red" }) {
  const { t } = useTranslation();
  return (
    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white ${STATUS_BG[severity]}`}>
      {t(`status.${severity}`)}
    </span>
  );
}

/** Deterministic-vs-AI provenance badge (requirement 9.6 / 9.10). */
export function ProvenanceBadge({ source }: { source: "rule" | "ai" | "human" }) {
  const { t } = useTranslation();
  const cls = source === "ai" ? "badge-ai" : source === "human" ? "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-brand-grey-light text-brand-charcoal" : "badge-rule";
  return <span className={cls}>{t(`provenance.${source}`)}</span>;
}

/** "Updated 3 minutes ago · Daily" freshness stamp. */
export function FreshnessStamp({ lastUpdated, refreshClass }: { lastUpdated: string | null; refreshClass?: string }) {
  const { t, i18n } = useTranslation();
  const date = parseFrappeDatetime(lastUpdated);
  const stale = date ? Date.now() - date.getTime() > 36 * 3600_000 : true;
  return (
    <span className={`freshness ${stale ? "freshness-stale" : ""}`} title={lastUpdated ?? ""}>
      {t("common.updated")} {fmtRelative(date, i18n.language)}
      {refreshClass ? ` · ${t(`refresh.${refreshClass}`, refreshClass)}` : ""}
    </span>
  );
}

export function Card({ children, className = "", ...rest }: { children: ReactNode; className?: string } & HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={`rounded-card border border-brand-grey-light bg-white shadow-sm ${className}`}>{children}</div>;
}

export function Select({ label, value, onChange, options, allLabel }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  allLabel?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-brand-grey">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-brand-grey-light bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal text-brand-charcoal focus:border-brand-primary focus:outline-none">
        {allLabel !== undefined && <option value="">{allLabel}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-brand-grey-light ${className}`} />;
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  const msg = error instanceof Error ? error.message : String(error);
  const unauthorised = /(Frappe|OneFleet) 40[13]/.test(msg);
  return (
    <div role="alert" className="rounded-card border border-status-red/40 bg-red-50 p-4 text-sm text-brand-charcoal">
      <div className="font-semibold text-status-red">{unauthorised ? t("common.forbidden") : t("common.error")}</div>
      <div className="mt-1 break-words text-xs text-brand-grey">{msg.slice(0, 300)}</div>
      {onRetry && (
        <button onClick={onRetry} className="mt-3 rounded bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-deep">
          {t("common.retry")}
        </button>
      )}
    </div>
  );
}

/** Tiny inline sparkline: values as a line, target as a dashed line, last point coloured by status. */
export function Sparkline({ values, targets, status, width = 120, height = 32 }: {
  values: (number | null)[];
  targets: (number | null)[];
  status: Status;
  width?: number;
  height?: number;
}) {
  const nums = [...values, ...targets].filter((v): v is number => v != null);
  if (nums.length < 2) return <svg width={width} height={height} aria-hidden="true" />;
  const min = Math.min(...nums), max = Math.max(...nums);
  const span = max - min || 1;
  const n = values.length;
  const x = (i: number) => (n === 1 ? width / 2 : 4 + (i * (width - 8)) / (n - 1));
  const y = (v: number) => height - 4 - ((v - min) / span) * (height - 8);
  const path = (arr: (number | null)[]) =>
    arr.map((v, i) => (v == null ? null : `${i === 0 || arr[i - 1] == null ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`)).filter(Boolean).join(" ");
  const last = values[n - 1];
  const stroke = status === "Red" ? "#D93A3A" : status === "Amber" ? "#E8930C" : status === "Green" ? "#17A65B" : "#8A94A6";
  return (
    <svg width={width} height={height} aria-hidden="true" className="overflow-visible">
      <path d={path(targets)} fill="none" stroke="#B8C0CC" strokeWidth="1" strokeDasharray="3 3" />
      <path d={path(values)} fill="none" stroke={brandColour("--brand-primary")} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
      {last != null && <circle cx={x(n - 1)} cy={y(last)} r="3" fill={stroke} />}
    </svg>
  );
}
